import { createStore } from 'vuex'
import { auth, db} from '@/firebase/gfbconf'
import moment from 'moment/moment';
import axios from 'axios';
import VuexPersistence from 'vuex-persist';
import offlineQueues from '@/offlineDriverMode/OfflineQueue.js'
import localforage from 'localforage';

const vuexLocal = new VuexPersistence({
  key: "DriverStorage",
  supportCircular: true,
  storage: localforage,
  reducer: (state) => ({offlineModeState: state.offlineModeState}),
})

let logoutTimer = null;
let notificationTimer = null;
const SESSION_KEY = 'janart_session';
const SESSION_RESTORE_NOTICE_KEY = 'janart_session_restore_notice';
const SESSION_TTL_MS = 8 * 60 * 60 * 1000;
const NOTIFICATION_DISPLAY_MS = 3000;
const DEFAULT_NOTIFICATION = {
  show: false,
  head: "",
  subheader: "",
  success: true,
};

function getSessionExpiryFromNow() {
  return Date.now() + SESSION_TTL_MS;
}

function saveSession(sessionData) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
  } catch (error) {
    console.error('saveSession error:', error);
  }
}

function getSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    console.error('getSession error:', error);
    return null;
  }
}

function clearSession() {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch (error) {
    console.error('clearSession error:', error);
  }
}

function rememberSessionRestoreNotice(uid) {
  try {
    localStorage.setItem(SESSION_RESTORE_NOTICE_KEY, uid || "active");
  } catch (error) {
    console.error('rememberSessionRestoreNotice error:', error);
  }
}

function shouldShowSessionRestoreNotice(uid) {
  try {
    const value = uid || "active";
    if(localStorage.getItem(SESSION_RESTORE_NOTICE_KEY) === value) return false;
    localStorage.setItem(SESSION_RESTORE_NOTICE_KEY, value);
    return true;
  } catch (error) {
    console.error('shouldShowSessionRestoreNotice error:', error);
    return false;
  }
}



let __reqIntAdded = false;
let __resIntAdded = false;

// Poczekaj raz na przywrócenie sesji po reloadzie
let __authReadyPromise;
if (!__authReadyPromise) {
  __authReadyPromise = new Promise((resolve) => {
    const unsub = auth.onAuthStateChanged(() => { unsub(); resolve(); });
  });
}

function isOurApiUrl(url, store) {
  const full = (url || '');
  return [store.state.apiLink, store.state.herokuApiLink]
    .some(link => link && full.startsWith(link));
}

// Rozpoznaj konkretne komunikaty z checkAccess
function classifyCheckAccessFailure(data) {
  const ok = data && data.success === false && typeof data.error === 'string';
  if (!ok) return null;
  const e = data.error;

  if (e.includes('Brak nagłówka Authorization Bearer.')) return 'missingAuth';
  if (e.includes('Nieprawidłowy identyfikator aplikacji.')) return 'badAppId';
  if (e.includes('Konto użytkownika jest wyłączone.')) return 'disabled';
  if (e.includes('Brak profilu userData.')) return 'noProfile';
  if (e.includes('initializedBy nie zgadza się z tożsamością tokenu.')) return 'uidMismatch';
  if (e.includes('Błąd autoryzacji lub weryfikacji użytkownika.')) return 'genericAuth';
  return null;
}

const authAxiosPlugin = (store) => {
  // REQUEST: x-app-id (zawsze) + świeży ID token (dla Twoich API)
  if (!__reqIntAdded) {
    axios.interceptors.request.use(
      async (config) => {
        config = config || {};
        config.headers = config.headers || {};

        // 1) x-app-id – zawsze z AKTUALNEGO store (możesz go rotować mutacją)
        const appId = 'janart_6692046951778248';
        config.headers['x-app-id'] = appId;

        // 2) Authorization – tylko gdy to Twoje API
        const url = (config.baseURL || '') + (config.url || '');
        if (isOurApiUrl(url, store) && !config.headers.Authorization) {
          await __authReadyPromise;               // czekaj aż Firebase przywróci sesję
          const user = auth.currentUser;
          if (user) {
            try {
              const token = await user.getIdToken(); // odświeży się automatycznie jeśli przeterminowany
              config.headers.Authorization = `Bearer ${token}`;
            } catch { /* jeśli się nie uda – poleci bez, obsłużymy w onFulfilled */ }
          }
        }

        return config;
      },
      (error) => Promise.reject(error)
    );
    __reqIntAdded = true;
  }

  // RESPONSE:
  //  A) 200 + success:false + komunikat z checkAccess → selektywny retry 1x
  //  B) 401 (awaryjnie) → retry 1x
  if (!__resIntAdded) {
    axios.interceptors.response.use(
      // onFulfilled — obsługa "200 z błędem w body" z checkAccess
      async (response) => {
        const original = response.config || {};
        const url = (original.baseURL || '') + (original.url || '');
        if (!isOurApiUrl(url, store)) return response;

        const type = classifyCheckAccessFailure(response.data);
        if (!type || original.__retried) return response;

        original.__retried = true;

        // zawsze użyj najświeższego x-app-id
        original.headers = { ...(original.headers || {}) };
        original.headers['x-app-id'] = 'janart_6692046951778248';

        // Dla problemów z autoryzacją – spróbuj odświeżyć token i powtórzyć
        if (type === 'missingAuth' || type === 'genericAuth' || type === 'badAppId') {
          await __authReadyPromise;
          const user = auth.currentUser;
          if (user) {
            try {
              // dla 'badAppId' też pobierz token – nie zaszkodzi
              const fresh = await user.getIdToken(true);
              original.headers.Authorization = `Bearer ${fresh}`;
              return axios.request(original);
            } catch { /* brak retry */ }
          }
        }

        // Dla 'disabled' / 'noProfile' / 'uidMismatch' nie retry’ujemy
        return response;
      },

      // onRejected — klasyczne błędy HTTP (np. 401)
      async (error) => {
        const original = error.config || {};
        const url = (original.baseURL || '') + (original.url || '');
        if (!isOurApiUrl(url, store) || original.__retried) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401) {
          original.__retried = true;
          await __authReadyPromise;
          const user = auth.currentUser;
          if (user) {
            try {
              const fresh = await user.getIdToken(true);
              original.headers = { ...(original.headers || {}) };
              original.headers['x-app-id'] = 'janart_6692046951778248';
              original.headers.Authorization = `Bearer ${fresh}`;
              return axios.request(original);
            } catch { /* upuszczamy do catcha */ }
          }
        }
        return Promise.reject(error);
      }
    );
    __resIntAdded = true;
  }
};

// Clear cricular structure error of vuex-persist
// window.localStorage.clear()

//changed window.localStorage to localforage to avoid circular structure error;

export default createStore({
  state: {
    // Adres API da sie podmienic zmienna srodowiskowa. Bez tego lokalny test panelu
    // wymagal recznej edycji tego pliku - a taka zmiana potrafi pojechac do repo
    // i przekierowac produkcje na czyjs localhost.
    // Uzycie: VUE_APP_API_LINK=http://localhost:5001/jan-art/europe-central2/api npm run serve:production_local
    apiLink: process.env.VUE_APP_API_LINK || "https://europe-central2-jan-art.cloudfunctions.net/api",
    herokuApiLink: "https://janart-app-2d9773139e0a.herokuapp.com/api",
    herokuApiPath: "production",
    notification: {
      show: false,
      head: "",
      subheader: "",
      success: true,
    },
    notificationQueue: [],
    // global state of driver offline mode
    isDriverOnline: true,
    sendingCallsFromQueue: false,
    //  OFFLINE MODE DATA FOR DRIVER (PERSISTED STATE)
    offlineModeState: {
      lastSaveUnix: null,
      backupStatuses: [],
      dataSnapshot: {
        totals: {
          all: null,
          loaded: null,
        },
        route: null,
        orders: null,
      },
      callsQueue: [],
    },
    callForPhotos: {
      order: null,
      taken: false,
    },
    callForScan: {
      order: null,
      taken: false,
    },
    callForSignature: {
      order: null,
      taken: false,
    },
    orderIndex: null,
    productOrdersCart: {},
    variantsCart: {},
    app: {
      active: true,
      version: '',
    },
    user: null,
    route: null,
    userData: null,
    isDriverOnline: false,
    authIsReady: false,
    sessionExpired: false,
    globalLoader: {
      show: false,
      head: "Loading..",
      subheader: "Don't close this tab."
    }
  },
  plugins: [vuexLocal.plugin,
    // authAxiosPlugin
  ],
  mutations: {
    clearDoneOfflineQueue(state)
    {
      if(state.offlineModeState.callsQueue.length === 0) return;
      for(let i=state.offlineModeState.callsQueue.length-1;i>=0;i--)
      {
        let current = state.offlineModeState.callsQueue[i];
        if(current.done === true)
        {
          state.offlineModeState.callsQueue.splice(i,1);
        }
      }
    },
    clearOfflineQueue(state)
    {
      state.offlineModeState.callsQueue = [];
    },
    async performOfflineQueue(state)
    {
      if(state.sendingCallsFromQueue) return;
      if(state.isDriverOnline === true && state.offlineModeState.callsQueue.length > 0)
      {
        state.sendingCallsFromQueue = true;
        for(let i=0;i<state.offlineModeState.callsQueue.length;i++)
        {
          let current = state.offlineModeState.callsQueue[i];
          if(current.done === false)
          {
            const result = await offlineQueues.triggerOfflineQueue(current);
            if(result === true)
            {
              current.done = true;
            }
            else
            {
              current.done = false;
            }
          }
        }
        state.sendingCallsFromQueue = false;
      }
      this.commit("clearDoneOfflineQueue");
    },
    addToOfflineCallsQueue(state, data)
    {
      if(data === undefined) return;

      let call = {
        unixTimestamp: moment().unix(),
        ...data
      };
      state.offlineModeState.callsQueue.push(call)
    },
    setRouteStatusesToDriverOfflineBackup(state,statuses)
    {
      state.offlineModeState.backupStatuses = [...statuses];
    },
    setIsDriverOnline(state,status)
    {
      state.isDriverOnline = status;
      if(status === true && state.offlineModeState.callsQueue.length > 0)
      {
        this.commit("performOfflineQueue");
      }
    },
    // CLEAR PERSIST STATE FOR DRIVER
    clearPersistStateDriver(state)
    {
      console.log("Stan offline wyczyszczony")
      localforage.removeItem('driverStorage');
      localforage.clear();
      state.offlineModeState.lastSaveUnix = null;
      state.offlineModeState.callsQueue = []
      state.offlineModeState.dataSnapshot = {
        totals: {
          all: null,
          loaded: null,
        },
        route: null,
        orders: null,        
      }
    },
    // CREATE OFFLINE SNAPSHOT FOR DRIVERS
    offlineModeSnapshotDriver(state, data)
    {
      
      state.offlineModeState.lastSaveUnix = moment().unix();
      state.offlineModeState.dataSnapshot.route = data.route;
      state.offlineModeState.dataSnapshot.orders = data.orders;
      state.offlineModeState.dataSnapshot.totals.all = data.totalOrdersAmount;
      state.offlineModeState.dataSnapshot.totals.loaded = data.loadedOrdersAmount;
      
      console.log("[OfflineMode/Driver] Snapshot done")
    },
    updateProductOrders(state, productOrdersCart) {
      state.productOrdersCart = productOrdersCart
    },
    setNotification(state, notification)
    {
      const nextNotification = {
        ...DEFAULT_NOTIFICATION,
        ...notification,
        show: notification?.show === false ? false : true,
      };

      if(nextNotification.show === false)
      {
        this.commit("closeNotification");
        return;
      }

      if(state.notification.show)
      {
        state.notificationQueue.push(nextNotification);
        return;
      }

      state.notification = nextNotification;

      if(notificationTimer)
      {
        clearTimeout(notificationTimer);
        notificationTimer = null;
      }

      const store = this;
      const displayTime = Number(nextNotification.duration) > 0 ? Number(nextNotification.duration) : NOTIFICATION_DISPLAY_MS;
      notificationTimer = setTimeout(() => {
        store.commit("closeNotification");
      }, displayTime);
    },
    closeNotification(state)
    {
      if(notificationTimer)
      {
        clearTimeout(notificationTimer);
        notificationTimer = null;
      }

      state.notification = { ...DEFAULT_NOTIFICATION };

      if(state.notificationQueue.length > 0)
      {
        const store = this;
        setTimeout(() => {
          store.commit("showNextNotification");
        }, 120);
      }
    },
    showNextNotification(state)
    {
      if(state.notification.show || state.notificationQueue.length === 0) return;
      const nextNotification = state.notificationQueue.shift();
      this.commit("setNotification", nextNotification);
    },
    clearNotifications(state)
    {
      if(notificationTimer)
      {
        clearTimeout(notificationTimer);
        notificationTimer = null;
      }

      state.notification = { ...DEFAULT_NOTIFICATION };
      state.notificationQueue = [];
    },
    setUrl(state, url)
    {
      state.url = url
    },
    // Set user to Vuex store
    setUser(state, userData)
    {
      state.user = userData;
    },
    setOrderIndex(state, payload)
    {
      state.orderIndex = payload;
    },
    updateInternetConnectionStatus(state, status)
    {
      state.internetConnection = status;
    },
    setUserCredentials(state, userData)
    {
      state.userData = userData;
    },
    setAppState(state, about)
    {
      state.app = {
        active: about.active !== undefined ? about.active : state.app.active,
        version: about.version !== undefined ? about.version : state.app.version,
      };
    },
    setAuthIsReady(state,payload)
    {
      state.authIsReady = payload;
    },
    setSessionExpired(state,payload)
    {
      state.sessionExpired = !!payload;
    },
    setRoute(state,payload)
    {
      state.route = payload;
    },
    // reset global loader
    resetGlobalLoader(state)
    {
      state.globalLoader = {
        show: false,
        head: "Ładowanie..",
        subheader: "Nie zamykaj tego okna."
      }
    },
    setGlobalLoader(state,context)
    {
      state.globalLoader = context;
    },
    addToCart(state, payload)
    {
      for(let i=0; i<payload.length; i++)
      {
        let isFound = false;
        for(let y=0; y<Object.keys(state.variantsCart).length; y++)
        {
          const products = Object.entries(state.variantsCart)
          if(products[y][0] === payload[i].id)
          {
            isFound = true;
            state.variantsCart[payload[i].id].printAmount++;
          }
        }
        if(!isFound)
        {
          state.variantsCart[payload[i].id] = payload[i]
        }
      }
    },
  },
  actions: {
    addVariantsToCart(context, payload)
    {
      context.commit("addToCart", payload);
      context.commit('setNotification',{
        show: true,
        head: "Akcja zakończona sukcesem!",
        subheader: 'Warianty zostały pomyślnie dodane do listy.',
        success: true
     });
    },
    async fetchUserData(context, uid)
    {
      const userCredentials = await db.collection("UsersData").doc(uid).get();
      if(!userCredentials.exists) throw new Error("Could not complete login action.");
      const data = userCredentials.data();
      context.commit('setUserCredentials', data);
      return data;
    },
    async setAutoLogout(context, expiresAt)
    {
      if(!expiresAt) return;

      const timeLeft = Number(expiresAt) - Date.now();

      if(logoutTimer)
      {
        clearTimeout(logoutTimer);
        logoutTimer = null;
      }

      if(timeLeft <= 0)
      {
        await context.dispatch("logout", { expired: true });
        return;
      }

      logoutTimer = setTimeout(async () => {
        await context.dispatch("logout", { expired: true });
      }, timeLeft);
    },
    clearAutoLogout()
    {
      if(logoutTimer)
      {
        clearTimeout(logoutTimer);
        logoutTimer = null;
      }
    },
    //login new user
    async login(context,payload)
    {
      context.commit("setSessionExpired", false);
      const signIn = await auth.signInWithEmailAndPassword(payload.email, payload.password);
      if(signIn) {
        context.commit('setGlobalLoader',{
          show: true,
          head: "Sprawdzanie twoich danych..",
          subheader: `Nie zamykaj okna.`,
          success: false
        });
        const expiresAt = getSessionExpiryFromNow();
        await context.dispatch("fetchUserData", signIn.user.uid);
        saveSession({
          uid: signIn.user.uid,
          email: signIn.user.email || "",
          expiresAt,
        });
        context.commit('setUser', signIn.user);
        context.commit("setAuthIsReady", true);
        rememberSessionRestoreNotice(signIn.user.uid);
        await context.dispatch("setAutoLogout", expiresAt);
        context.commit('setNotification',{
          show: true,
          head: "Pomyślnie zalogowano",
          subheader: "Witaj w panelu Jan-Art.",
          success: true
        });
      }
      else
      {
        throw new Error("Could not complete login action.")
      };
    },
    // logout user;
    async logout(context, options = {})
    {
      const expired = !!options.expired;
      context.dispatch("clearAutoLogout");
      clearSession();
      context.commit("setSessionExpired", expired);
      await auth.signOut();
      context.commit('setUser', null);
      context.commit('setUserCredentials', null);
      context.commit("setAuthIsReady", true);
      setTimeout(() => {
        context.commit("resetGlobalLoader");
      }, 1000);
      if(expired)
      {
        context.commit('setNotification',{
          show: true,
          head: "Sesja wygasła",
          subheader: "Zaloguj się ponownie, aby kontynuować.",
          success: false
        });
      }
      else
      {
        context.commit('setNotification',{
          show: true,
          head: "Pomyślnie wylogowano",
          subheader: "Sesja została bezpiecznie zakończona.",
          success: true
        });
      }
    },
    initAuth(context)
    {
      return new Promise((resolve) => {
        let resolved = false;

        const unsubscribe = auth.onAuthStateChanged(async (user) => {
          try {
            const session = getSession();

            if(!session)
            {
              if(user)
              {
                const expiresAt = getSessionExpiryFromNow();
                saveSession({
                  uid: user.uid,
                  email: user.email || "",
                  expiresAt,
                });

                context.commit("setSessionExpired", false);
                context.commit("setUser", user);
                await context.dispatch("fetchUserData", user.uid);
                await context.dispatch("setAutoLogout", expiresAt);
                context.commit("setAuthIsReady", true);
                if(shouldShowSessionRestoreNotice(user.uid))
                {
                  context.commit('setNotification',{
                    show: true,
                    head: "Sesja została przywrócona",
                    subheader: "Możesz kontynuować pracę w panelu Jan-Art.",
                    success: true
                  });
                }

                if(!resolved)
                {
                  resolved = true;
                  resolve(user);
                  unsubscribe();
                }
                return;
              }

              context.dispatch("clearAutoLogout");
              context.commit("setUser", null);
              context.commit("setUserCredentials", null);
              context.commit("setAuthIsReady", true);

              if(!resolved)
              {
                resolved = true;
                resolve(null);
                unsubscribe();
              }
              return;
            }

            if(!session.expiresAt || Number(session.expiresAt) <= Date.now())
            {
              context.dispatch("clearAutoLogout");
              clearSession();
              context.commit("setSessionExpired", true);

              if(user)
              {
                await auth.signOut();
              }

              context.commit("setUser", null);
              context.commit("setUserCredentials", null);
              context.commit("setAuthIsReady", true);
              context.commit('setNotification',{
                show: true,
                head: "Sesja wygasła",
                subheader: "Zaloguj się ponownie, aby kontynuować.",
                success: false
              });

              if(!resolved)
              {
                resolved = true;
                resolve(null);
                unsubscribe();
              }
              return;
            }

            if(user)
            {
              const refreshedExpiry = getSessionExpiryFromNow();
              saveSession({
                uid: user.uid,
                email: user.email || "",
                expiresAt: refreshedExpiry,
              });

              context.commit("setSessionExpired", false);
              context.commit("setUser", user);
              await context.dispatch("fetchUserData", user.uid);
              await context.dispatch("setAutoLogout", refreshedExpiry);
              context.commit("setAuthIsReady", true);
              if(shouldShowSessionRestoreNotice(user.uid))
              {
                context.commit('setNotification',{
                  show: true,
                  head: "Sesja została przywrócona",
                  subheader: "Możesz kontynuować pracę w panelu Jan-Art.",
                  success: true
                });
              }

              if(!resolved)
              {
                resolved = true;
                resolve(user);
                unsubscribe();
              }
              return;
            }

            context.commit("setUser", null);
            context.commit("setUserCredentials", null);
            context.commit("setAuthIsReady", true);

            if(!resolved)
            {
              resolved = true;
              resolve(null);
              unsubscribe();
            }
          } catch (error) {
            console.error("initAuth error:", error);
            context.commit("setUser", null);
            context.commit("setUserCredentials", null);
            context.commit("setAuthIsReady", true);

            if(!resolved)
            {
              resolved = true;
              resolve(null);
              unsubscribe();
            }
          }
        });
      });
    },
    // update order history;
    async updateHistory(context, payload) {
      try {
        const {collectionName, docId, message, details} = payload
        const routeId = payload.routeId || null

        await db.collection(collectionName).doc(docId).collection("History").doc().set({
          message,
          details,
          routeId,
          meta: {
            createdDate: moment().toDate(),
            lastModificationDate: moment().toDate(),
            createdBy: `${context.state.userData.name} ${context.state.userData.surname}`,
            userId: context.state.userData.id,
          }
        });
      } catch (error) {
        console.log(error);
      }
    },
  },
  modules: {
  }
})
